export const scopeBuckets = [
  'userGlobal',
  'userAccount',
  'accountExact',
  'parentExact',
  'parentChildren',
  'other',
] as const;

export type ScopeBucket = (typeof scopeBuckets)[number];

/** Two top-level groupings for the UI */
export const topLevelBuckets = ['user', 'account'] as const;
export type TopLevelBucket = (typeof topLevelBuckets)[number];

/** Maps scopeBucket → which top-level bucket it belongs to */
export const scopeToTopLevel: Record<ScopeBucket, TopLevelBucket> = {
  userGlobal: 'user',
  userAccount: 'user',
  accountExact: 'account',
  parentExact: 'account',
  parentChildren: 'account',
  other: 'account',
};

/** Short tag label for each scope (shown as chip on report rows) */
export const scopeTagLabels: Record<ScopeBucket, string> = {
  userGlobal: 'Global',
  userAccount: 'This account',
  accountExact: 'Account',
  parentExact: 'Parent',
  parentChildren: 'Parent + children',
  other: 'Other',
};

/** Section labels for the two top-level buckets (legacy grouped saved-reports layout) */
export const topLevelLabels: Record<TopLevelBucket, string> = {
  user: 'My reports',
  account: 'Shared',
};

/** Custom reports tab scope filter menu (maps to top-level buckets). */
export const customReportsScopeFilterLabels = {
  mine: topLevelLabels.user,
  account: topLevelLabels.account,
} as const;

/** Saved reports grid: who can access the report (derived from scopeBucket via scopeToTopLevel) */
export const viewUserAccessLabels: Record<TopLevelBucket, string> = {
  user: 'Only Me',
  account: 'Everyone on this account',
};

export function getViewUserAccessLabel(scopeBucket?: ScopeBucket | null): string {
  const topLevel = scopeToTopLevel[(scopeBucket ?? 'other') as ScopeBucket];
  return viewUserAccessLabels[topLevel];
}

/** Sort rank for saved reports lists: Only Me (0) before Everyone on this account (1). */
export function getViewUserAccessSortRank(scopeBucket?: ScopeBucket | null): number {
  const topLevel = scopeToTopLevel[(scopeBucket ?? 'other') as ScopeBucket];
  return topLevel === 'user' ? 0 : 1;
}

/** Ribbon behind section titles (saved-reports grid groups, schedules pages, etc.) */
export const analyticsStatsSectionHeadingSx = {
  py: 1,
  px: 1.5,
  mb: 1,
  borderLeft: '4px solid',
  borderColor: 'dianomiGreen.main',
  bgcolor: 'grey.100',
} as const;
